import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Form,
  Dropdown,
  Badge,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";

const BoostManagement = () => {
  const [boosts, setBoosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    boost_type: "",
    status: "",
  });
  const [sorting, setSorting] = useState({
    sort_by: "created_at",
    sort_order: "desc",
  });
  const [stats, setStats] = useState({
    totalBoosts: 0,
    totalRevenue: 0,
    totalCost: 0,
    profit: 0,
    statusCounts: {},
    typeCounts: {},
  });
  const [pagination, setPagination] = useState({
    lastId: null,
    hasNextPage: false,
    pageSize: 100
  });

  // Fetch boosts with current filters, sorting and pagination
  const fetchBoosts = async (loadMore = false) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();

      // Add filters if they exist
      if (filters.boost_type)
        queryParams.append("boost_type", filters.boost_type);
      if (filters.status) queryParams.append("status", filters.status);

      // Add sorting
      queryParams.append("sort_by", sorting.sort_by);
      queryParams.append("sort_order", sorting.sort_order);

      // Add pagination
      queryParams.append("page_size", pagination.pageSize);
      if (loadMore && pagination.lastId) {
        queryParams.append("last_id", pagination.lastId);
      }

      const response = await axiosClient.get(
        `https://api.viralgpt.com/admin/boosts?${queryParams.toString()}`,
      );

      const newBoosts = response.data.data;
      setBoosts(loadMore ? [...boosts, ...newBoosts] : newBoosts);
      setPagination({
        ...pagination,
        lastId: response.data.pagination.next_cursor,
        hasNextPage: response.data.pagination.has_next_page
      });

      if (!loadMore) {
        calculateStats(newBoosts);
      }
    } catch (error) {
      console.error("Error fetching boosts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load more boosts
  const loadMore = () => {
    fetchBoosts(true);
  };

  // Calculate statistics from boost data
  const calculateStats = (boostData) => {
    const stats = {
      totalBoosts: boostData.length,
      totalRevenue: 0,
      totalCost: 0,
      profit: 0,
      statusCounts: {},
      typeCounts: {},
    };

    boostData.forEach((boost) => {
      // Calculate financial metrics
      stats.totalRevenue += parseFloat(boost.price) || 0;
      stats.totalCost += parseFloat(boost.cost) || 0;

      // Count by status
      stats.statusCounts[boost.status] =
        (stats.statusCounts[boost.status] || 0) + 1;

      // Count by boost type
      const boostType = boost.boost_type;
      stats.typeCounts[boostType] = (stats.typeCounts[boostType] || 0) + 1;
    });

    stats.profit = stats.totalRevenue - stats.totalCost;
    setStats(stats);
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value,
    });
    setPagination({
      ...pagination,
      lastId: null
    });
  };

  // Handle sorting changes
  const handleSortChange = (column) => {
    setSorting({
      sort_by: column,
      sort_order:
        sorting.sort_by === column && sorting.sort_order === "asc"
          ? "desc"
          : "asc",
    });
    setPagination({
      ...pagination,
      lastId: null
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      boost_type: "",
      status: "",
    });
    setPagination({
      ...pagination,
      lastId: null
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "in_progress":
        return "primary";
      case "partial":
        return "info";
      case "canceled":
        return "danger";
	  case "processing":
		return "info";
      default:
        return "secondary";
    }
  };

  // Get platform icon
  const getPlatformIcon = (boostType) => {
    if (!boostType) return "fa-question";

    const platform = boostType.split("-")[0];
    switch (platform) {
      case "instagram":
        return "fa-instagram";
      case "tiktok":
        return "fa-tiktok";
      case "youtube":
        return "fa-youtube";
      case "twitter":
        return "fa-twitter";
      default:
        return "fa-globe";
    }
  };

  // Get action name from boost type
  const getActionName = (boostType) => {
    if (!boostType) return "";

    const parts = boostType.split("-");
    return parts.length > 1
      ? parts[1].charAt(0).toUpperCase() + parts[1].slice(1)
      : "";
  };

  // Load boosts when component mounts or filters/sorting change
  useEffect(() => {
    fetchBoosts();
  }, [filters, sorting]);

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col md={3}>
          <div className="h-100" style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)'}}>
            <div style={{padding:'1.5rem'}}>
              <div style={{fontSize:'0.8rem',fontWeight:600,color:'#425466',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'1rem'}}>Boost Stats</div>
              <div className="d-flex flex-column gap-2 mt-3">
                <div className="d-flex justify-content-between">
                  <span>Total Boosts:</span>
                  <span className="fw-bold">{stats.totalBoosts}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Total Revenue:</span>
                  <span className="fw-bold text-success">
                    ${stats.totalRevenue.toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Total Cost:</span>
                  <span className="fw-bold text-danger">
                    ${stats.totalCost.toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Profit:</span>
                  <span
                    className={`fw-bold ${stats.profit >= 0 ? "text-success" : "text-danger"}`}
                  >
                    ${stats.profit.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col md={3}>
          <div className="h-100" style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)'}}>
            <div style={{padding:'1.5rem'}}>
              <div style={{fontSize:'0.8rem',fontWeight:600,color:'#425466',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'1rem'}}>Status Distribution</div>
              <div className="d-flex flex-column gap-2 mt-3">
                {Object.entries(stats.statusCounts).map(([status, count]) => (
                  <div
                    key={status}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <Badge bg={getStatusBadgeColor(status)}>{status}</Badge>
                    <span>
                      {count} ({((count / stats.totalBoosts) * 100).toFixed(1)}
                      %)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>

        <Col md={6}>
          <div className="h-100" style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)'}}>
            <div style={{padding:'1.5rem'}}>
              <div style={{fontSize:'0.8rem',fontWeight:600,color:'#425466',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'1rem'}}>Boost Type Distribution</div>
              <div className="d-flex flex-column gap-2 mt-3">
                {Object.entries(stats.typeCounts).map(([type, count]) => (
                  <div
                    key={type}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <i className={`fab ${getPlatformIcon(type)} me-2`}></i>
                      {type}
                    </div>
                    <span>
                      {count} ({((count / stats.totalBoosts) * 100).toFixed(1)}
                      %)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <div className="mb-4" style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)'}}>
        <div style={{padding:'1.5rem'}}>
          <div style={{fontSize:'0.8rem',fontWeight:600,color:'#425466',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'1rem'}}>Boost Filters</div>
          <Row className="mt-3">
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Boost Type</Form.Label>
                <Form.Select
                  value={filters.boost_type}
                  onChange={(e) =>
                    handleFilterChange("boost_type", e.target.value)
                  }
                >
                  <option value="">All Types</option>
                  {Object.keys(stats.typeCounts).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {Object.keys(stats.statusCounts).map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4} className="d-flex align-items-end">
              <button
                style={{background:'transparent',border:'1px solid #e3e8ee',borderRadius:'10px',color:'#425466',fontWeight:600,fontSize:'0.85rem',padding:'0.5rem 1rem',cursor:'pointer',width:'100%',marginBottom:'1rem'}}
                onClick={resetFilters}
              >
                Reset Filters
              </button>
            </Col>
          </Row>
        </div>
      </div>

      <div style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)'}}>
        <div style={{padding:'1.5rem'}}>
          <div style={{fontSize:'0.8rem',fontWeight:600,color:'#425466',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'1rem'}}>Boost Orders</div>

          {loading && !pagination.lastId ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <>
              {/* Desktop view */}
              <div className="table-responsive d-none d-md-block">
                <Table hover className="mt-3">
                  <thead>
                    <tr>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("id")}
                      >
                        ID
                        {sorting.sort_by === "id" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("userid")}
                      >
                        User ID
                        {sorting.sort_by === "userid" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("boost_type")}
                      >
                        Type
                        {sorting.sort_by === "boost_type" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th>Link</th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("quantity")}
                      >
                        Quantity
                        {sorting.sort_by === "quantity" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
					  <th>Start Count</th>
					  <th>Current Count</th>
					  <th>Remains</th>

                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("price")}
                      >
                        Price
                        {sorting.sort_by === "price" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("status")}
                      >
                        Status
                        {sorting.sort_by === "status" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("created_at")}
                      >
                        Date
                        {sorting.sort_by === "created_at" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {boosts.map((boost) => (
                      <tr key={boost.id}>
                        <td>{boost.id}</td>
                        <td>{boost.userid}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <i
                              className={`fab ${getPlatformIcon(boost.boost_type)} me-2`}
                            ></i>
                            {getActionName(boost.boost_type)}
                          </div>
                        </td>
                        <td>
                          <a
                            href={boost.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-truncate d-inline-block text-primary"
                            style={{ maxWidth: "200px" }}
                            title={boost.link}
                          >
                            <i className="fas fa-link me-1"></i>

                          </a>
                        </td>
                        <td>{boost.quantity}</td>
						<td>{boost.start_count}</td>
						<td>{parseInt(boost.start_count) + (parseInt(boost.quantity) - parseInt(boost.remains))}</td>
						<td>{boost.remains}</td>

                        <td>${parseFloat(boost.price).toFixed(2)}</td>
                        <td>
                          <Badge bg={getStatusBadgeColor(boost.status)}>
                            {boost.status.charAt(0).toUpperCase() + boost.status.slice(1)}
                          </Badge>
                        </td>
                        <td>{formatDate(boost.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile view */}
              <div className="d-md-none">
                {boosts.map((boost) => (
                  <div key={boost.id} className="mb-3" style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)'}}>
                    <div style={{padding:'1.5rem'}}>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">ID: {boost.id}</span>
                        <span className="ms-2 text-muted">
                          User: {boost.userid}
                        </span>
                        <Badge bg={getStatusBadgeColor(boost.status)}>
                          {boost.status.charAt(0).toUpperCase() + boost.status.slice(1)}
                        </Badge>
                      </div>

                      <div className="mb-2">
                        <i
                          className={`fab ${getPlatformIcon(boost.boost_type)} me-2`}
                        ></i>
                        <span className="fw-bold">
                          {getActionName(boost.boost_type)}
                        </span>
                      </div>

                      <div className="mb-2 text-truncate">
                        <small className="text-muted">Link: </small>
                        <a
                          href={boost.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {boost.link}
                        </a>
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <div>
                          <small className="text-muted">Quantity: </small>
                          {boost.quantity}
                        </div>
                        <div>
                          <small className="text-muted">Price: </small>$
                          {parseFloat(boost.price).toFixed(2)}
                        </div>
                      </div>

                      <div className="text-muted small">
                        {formatDate(boost.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {boosts.length === 0 ? (
                <div className="text-center my-4">
                  <p>No boost orders found matching the current filters.</p>
                  <button style={{background:'transparent',border:'1px solid #e3e8ee',borderRadius:'10px',color:'#425466',fontWeight:600,fontSize:'0.85rem',padding:'0.5rem 1rem',cursor:'pointer'}} onClick={resetFilters}>
                    Reset Filters
                  </button>
                </div>
              ) : pagination.hasNextPage && (
                <div className="text-center mt-4">
                  <button 
                    style={{background:'transparent',border:'1px solid #e3e8ee',borderRadius:'10px',color:'#425466',fontWeight:600,fontSize:'0.85rem',padding:'0.5rem 1rem',cursor:'pointer'}}
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Container>
  );
};

export default BoostManagement;
